import { Circle as CircleStyle, Fill, Stroke, Style } from 'ol/style';
import { getPointRadius, noOverlapOffset } from '@utils/map/helpers';

import { DETECTION_POINTER, FALLBACK_OBJECT_COLOR } from '../constants';

/** Fixed radius for plain VectorLayer rendering (CircleStyle expects a number,
 *  not a WebGL expression). The per-image overlay is always viewed at a
 *  consistent zoom so zoom-responsive scaling isn't needed. */
const PLAIN_POINT_RADIUS = 5;

/**
 * WebGL flat-style builders for the collection detection layers.
 *
 * Each detection object is colored by its `node_uuid` via a `match`
 * expression. Every object renders at its own saved color + opacity always, so
 * the base style is a faithful, panel-independent picture of the collection.
 * A `liveStyling` override is layered on only while the user is actively editing
 * one object's color/opacity, giving a real-time preview without affecting the
 * rest of the collection.
 *
 * **Colors are style variables, not literals in the expression.** `setStyle`
 * disposes the layer's renderer — a shader recompile plus a buffer rebuild for
 * every feature — while `updateStyleVariables` writes uniforms and re-renders.
 * Editing a color previews on the map, and a hex keystroke previews per
 * keystroke, across three layers holding every visible detection. So
 * `buildShapeStyles` depends on the object *set* and nothing else, and
 * `buildShapeVariables` carries the colors.
 */

const STROKE_WIDTH = 2;

/**
 * Fill opacity per shape — `null` means "no fill", i.e. a center point drawn as
 * a circle. Declared once because all three builders below iterate the same
 * three shapes with the same fill opacities.
 */
const SHAPE_FILL_OPACITY = Object.freeze({
	[DETECTION_POINTER.CENTER_POINT]: null,
	[DETECTION_POINTER.OUTLINE]: 0.2,
	[DETECTION_POINTER.BOUNDING_BOX]: 0.1,
});

/** Runs `build` once per shape, keyed by shape. */
const byShape = build =>
	Object.fromEntries(
		Object.entries(SHAPE_FILL_OPACITY).map(([shape, fillOpacity]) => [
			shape,
			build(fillOpacity),
		])
	);

/**
 * Variable names are positional, because a uniform name has to be a GLSL
 * identifier and an object key (a node uuid, hyphens and all) is not one. That is what ties the style to
 * the object set: add or reorder an object and the style must be rebuilt, but
 * changing a color only moves a variable.
 */
const strokeVar = index => `stroke${index}`;
const fillVar = index => `fill${index}`;

/**
 * Expands a 3-digit hex color to 6 digits. Returns the input unchanged if it is
 * already 6 digits or not a recognized hex string.
 *
 * @param {string} hex - e.g. '#abc' → '#aabbcc'
 * @returns {string}
 */
const expandHex = hex => {
	if (typeof hex !== 'string') return hex;
	const match = hex.match(/^#([0-9a-f]{3})$/i);
	if (!match) return hex;
	const [r, g, b] = match[1].split('');
	return `#${r}${r}${g}${g}${b}${b}`;
};

/**
 * Converts a hex color string to an rgba string with the given opacity.
 * Supports both 3-digit and 6-digit hex.
 *
 * @param {string} hex
 * @param {number} opacity - 0–1
 * @returns {string}
 */
const hexToRgba = (hex, opacity) => {
	const full = expandHex(hex ?? FALLBACK_OBJECT_COLOR);
	const r = parseInt(full.slice(1, 3), 16);
	const g = parseInt(full.slice(3, 5), 16);
	const b = parseInt(full.slice(5, 7), 16);
	return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

// When alpha is 1 (fully opaque), the hex string is passed directly — WebGL flat
// style accepts hex and this avoids an unnecessary rgba conversion.
const toColor = (color, alpha) =>
	alpha === 1 ? (color ?? FALLBACK_OBJECT_COLOR) : hexToRgba(color, alpha);

/**
 * Resolves each object's effective color + alpha (0–1), applying the live
 * styling override to the object currently being edited.
 *
 * @param {Array<{key: string, color?: string, opacity?: number}>} objects
 * @param {{ objectKey: string, color?: string, opacity?: number }|null} liveStyling
 * @returns {Array<{key: string, color?: string, alpha: number}>}
 */
export const resolveObjects = (objects, liveStyling) =>
	(objects ?? []).map(obj => {
		const isEditing = liveStyling && obj.key === liveStyling.objectKey;
		const color = isEditing ? (liveStyling.color ?? obj.color) : obj.color;
		// opacity is 0–100 from the form / saved config; convert to 0–1
		const opacity = (isEditing ? (liveStyling.opacity ?? obj.opacity) : obj.opacity) ?? 100;
		return { key: obj.key, color, alpha: opacity / 100 };
	});

/**
 * Builds a per-node_uuid `match` expression whose outputs are style variables,
 * or a flat fallback when there are no objects. The expected type of a
 * `*-color` style property is a color, so each `['var', ...]` compiles to a
 * `vec4` uniform read at render time.
 *
 * @param {Array<{key: string}>} objects
 * @param {(index: number) => string} varName - strokeVar or fillVar
 */
const matchExpression = (objects, varName) =>
	objects.length > 0
		? [
				'match',
				['get', 'node_uuid'],
				...objects.flatMap(({ key }, index) => [key, ['var', varName(index)]]),
				FALLBACK_OBJECT_COLOR,
			]
		: FALLBACK_OBJECT_COLOR;

/**
 * Builds the WebGL flat style for one shape. Colors are left as variables, so
 * this depends only on which objects exist and in what order.
 *
 * @param {Array<{key: string}>} objects
 * @param {number|null} fillOpacity - fill opacity for polygon shapes; null → point
 * @returns {object} WebGL flat style
 */
const buildFlatStyle = (objects, fillOpacity) => {
	const stroke = matchExpression(objects, strokeVar);

	if (fillOpacity == null) {
		// Center points — rendered as circles.
		return {
			'circle-fill-color': stroke,
			'circle-radius': getPointRadius(),
			'stroke-color': stroke,
			'stroke-width': STROKE_WIDTH,
			'stroke-offset': noOverlapOffset(STROKE_WIDTH),
		};
	}

	// Polygon shapes (outline / bounding box) — translucent fill.
	return {
		'stroke-color': stroke,
		'stroke-width': STROKE_WIDTH,
		'stroke-offset': noOverlapOffset(STROKE_WIDTH),
		'fill-color': matchExpression(objects, fillVar),
	};
};

/**
 * Builds the flat styles for all three shapes in one pass. Takes no colors: they
 * arrive as variables, from `buildShapeVariables`.
 *
 * @param {Array<{key: string}>} objects - collection objects
 * @returns {{ center_point: object, outline: object, bounding_box: object }}
 */
export const buildShapeStyles = objects =>
	byShape(fillOpacity => buildFlatStyle(objects ?? [], fillOpacity));

/** The variable values one shape's style needs, from already-resolved objects. */
const shapeVariables = (resolved, fillOpacity) => {
	const variables = {};
	resolved.forEach(({ color, alpha }, index) => {
		variables[strokeVar(index)] = toColor(color, alpha);
		if (fillOpacity != null) variables[fillVar(index)] = toColor(color, alpha * fillOpacity);
	});
	return variables;
};

/**
 * Builds every shape's style variables — the colors that `buildShapeStyles`
 * left as `['var', ...]` references, resolved with the live edit applied.
 * Pushed with `updateStyleVariables`, which is what makes a color change (a
 * preview frame, a save landing) cost a uniform write instead of a recompile.
 *
 * Positional, so it must be built from the same `objects` array as the style.
 *
 * @param {Array<{key, color?, opacity?}>} objects - collection objects
 * @param {{ objectKey, color?, opacity? }|null} liveStyling - live edit override
 * @returns {{ center_point: object, outline: object, bounding_box: object }}
 */
export const buildShapeVariables = (objects, liveStyling = null) => {
	const resolved = resolveObjects(objects, liveStyling);
	return byShape(fillOpacity => shapeVariables(resolved, fillOpacity));
};

/**
 * Plain OL `Style` counterpart to buildShapeStyles, for consumers rendering
 * with a regular VectorLayer instead of WebGL (e.g. the single-image per-image
 * overlay, where the feature count is small and WebGL's shader-based rendering
 * isn't needed). Built from the same `resolveObjects` output, so color/opacity
 * resolution (including the live-editing override) is identical to the
 * WebGL map-level path — this only differs in which OL styling API it targets.
 *
 * @param {Array<{key: string, color?: string, opacity?: number}>} resolved
 * @returns {(feature: import('ol/Feature').default) => Style}
 */
const buildPlainStyleFunction = (resolved, fillOpacity) => {
	const colorByKey = new Map(resolved.map(({ key, color, alpha }) => [key, { color, alpha }]));
	const colorFor = (key, alphaScale = 1) => {
		const match = colorByKey.get(key);
		return toColor(match?.color, (match?.alpha ?? 1) * alphaScale);
	};

	if (fillOpacity == null) {
		// Center points — rendered as circles.
		return feature => {
			const color = colorFor(feature.get('node_uuid'));
			return new Style({
				image: new CircleStyle({
					radius: PLAIN_POINT_RADIUS,
					fill: new Fill({ color }),
					stroke: new Stroke({ color, width: STROKE_WIDTH }),
				}),
			});
		};
	}

	// Polygon shapes (outline / bounding box) — translucent fill.
	return feature => {
		const key = feature.get('node_uuid');
		return new Style({
			stroke: new Stroke({ color: colorFor(key), width: STROKE_WIDTH }),
			fill: new Fill({ color: colorFor(key, fillOpacity) }),
		});
	};
};

/**
 * Builds the plain-`Style` functions for all three shapes in one pass — the
 * non-WebGL sibling of buildShapeStyles.
 *
 * @param {Array<{key, color?, opacity?}>} objects - collection objects
 * @param {{ objectKey, color?, opacity? }|null} liveStyling - live edit override
 * @returns {{ center_point: Function, outline: Function, bounding_box: Function }}
 */
export const buildShapeStyleFunction = (objects, liveStyling = null) => {
	const resolved = resolveObjects(objects, liveStyling);
	return byShape(fillOpacity => buildPlainStyleFunction(resolved, fillOpacity));
};
